/**
 * Resolve navigation target for a platform notification row.
 * Type-specific auction routes override stored redirect_url (legacy rows may be stale).
 */

function auctionIdFromNotification(notification) {
  const refType = String(notification.reference_type || notification.referenceType || "").trim();
  const refId = notification.reference_id ?? notification.referenceId;
  if (refType !== "auction" || refId == null || refId === "") return null;
  const id = Math.trunc(Number(refId));
  return Number.isFinite(id) && id > 0 ? id : null;
}

function resolveApprovalNotificationPath(notification) {
  const moduleName = String(notification.module_name || notification.moduleName || "").trim();
  if (moduleName !== "approval") return null;

  const meta = notification.metadata || {};
  const auctionIdRaw = meta.auction_id ?? meta.auctionId;
  const auctionId =
    auctionIdRaw != null && Number.isFinite(Number(auctionIdRaw))
      ? Math.trunc(Number(auctionIdRaw))
      : null;
  const txType = String(meta.transaction_type || meta.transactionType || "").trim();

  if (auctionId != null && txType === "auction_award") {
    return `/auction/${auctionId}/review-bids`;
  }
  if (auctionId != null && (txType === "auction_sale" || txType === "auction_bid")) {
    return "/auction/my/auctions";
  }

  const refId = notification.reference_id ?? notification.referenceId;
  if (refId) return `/dashboard/p2p/my-approvals?requestId=${refId}`;
  return null;
}

function resolveBidAcceptedPath(notification, auctionId) {
  if (auctionId == null) return "/auction/my/auctions";

  const meta = notification.metadata || {};
  const role = String(meta.recipient_role || meta.recipientRole || "").trim();
  if (role === "seller") return `/auction/${auctionId}/proforma-invoice`;
  if (role === "buyer") return `/auction/${auctionId}/payment/process`;

  const message = String(notification.message || "").toLowerCase();
  if (message.includes("you have accepted")) {
    return `/auction/${auctionId}/proforma-invoice`;
  }
  if (message.includes("your bid")) {
    return `/auction/${auctionId}/payment/process`;
  }

  return `/auction/${auctionId}/proforma-invoice`;
}

function resolveAuctionNotificationPath(notification) {
  const moduleName = String(notification.module_name || notification.moduleName || "").trim();
  if (moduleName !== "auction") return null;

  const notificationType = String(
    notification.notification_type || notification.notificationType || "",
  ).trim();
  const auctionId = auctionIdFromNotification(notification);

  switch (notificationType) {
    case "new_bid_received":
    case "auction_bidding_closed":
    case "auction_ended":
    case "seller_decision_deadline_expired":
    case "bid_reopened":
      return "/auction/my/auctions";
    case "reserve_price_reached":
      return auctionId != null ? `/auction/${auctionId}/review-bids` : "/auction/my/auctions";
    case "bid_accepted":
      return resolveBidAcceptedPath(notification, auctionId);
    case "invoice_sent":
      return auctionId != null
        ? `/auction/${auctionId}/proforma-invoice`
        : "/auction/my/auctions";
    case "payment_completed":
      return auctionId != null
        ? `/auction/${auctionId}/collection-delivery`
        : "/auction/my/auctions";
    case "handover_confirmed":
    case "collection_confirmed":
      return auctionId != null
        ? `/auction/${auctionId}/collection-delivery`
        : "/auction/my/auctions";
    case "logistics_rfq_available":
      return auctionId != null
        ? `/logistics/rfq/generic?auctionId=${auctionId}&sourceType=auction_award`
        : "/auction/my/auctions";
    default:
      return auctionId != null ? `/auction/${auctionId}` : null;
  }
}

function resolveLogisticsNotificationPath(notification) {
  const moduleName = String(notification.module_name || notification.moduleName || "").trim();
  if (moduleName !== "logistics") return null;

  const meta = notification.metadata || {};
  const submissionIdRaw =
    meta.submission_id ?? meta.logistics_generic_rfq_id ?? meta.logisticsGenericRfqId;
  const refType = String(notification.reference_type || notification.referenceType || "").trim();
  const refId = notification.reference_id ?? notification.referenceId;
  const submissionId =
    submissionIdRaw != null && Number.isFinite(Number(submissionIdRaw))
      ? Math.trunc(Number(submissionIdRaw))
      : refType === "logistics_generic_rfq" && refId != null
        ? Math.trunc(Number(refId))
        : null;
  const quoteIdRaw = meta.quote_id ?? meta.logistics_quote_id ?? meta.logisticsQuoteId;
  const quoteId =
    quoteIdRaw != null && Number.isFinite(Number(quoteIdRaw))
      ? Math.trunc(Number(quoteIdRaw))
      : null;
  const manifestIdRaw = meta.manifest_id ?? meta.shipping_manifest_id;
  const manifestId =
    manifestIdRaw != null && Number.isFinite(Number(manifestIdRaw))
      ? Math.trunc(Number(manifestIdRaw))
      : refType === "shipping_manifest" && refId != null
        ? Math.trunc(Number(refId))
        : null;
  const grnIdRaw = meta.grn_id ?? meta.grnId;
  const grnId =
    grnIdRaw != null && Number.isFinite(Number(grnIdRaw))
      ? Math.trunc(Number(grnIdRaw))
      : refType === "grn" && refId != null
        ? Math.trunc(Number(refId))
        : null;
  const ewayBillIdRaw = meta.eway_bill_id ?? meta.ewayBillId;
  const ewayBillId =
    ewayBillIdRaw != null && Number.isFinite(Number(ewayBillIdRaw))
      ? Math.trunc(Number(ewayBillIdRaw))
      : refType === "eway_bill" && refId != null
        ? Math.trunc(Number(refId))
        : null;
  const notificationType = String(
    notification.notification_type || notification.notificationType || "",
  ).trim();
  const recipientRole = String(meta.recipient_role || meta.recipientRole || "").trim();

  switch (notificationType) {
    case "rfq_received":
      return submissionId != null
        ? `/dashboard/logistics/rfqs/${submissionId}/quote`
        : "/dashboard/logistics/rfqs";
    case "logistics_rfq_submitted":
    case "logistics_pickup_confirmed":
    case "logistics_invoice_generated":
    case "logistics_complete_payment":
      return submissionId != null
        ? `/logistics-rfqs-sent?rfqId=${submissionId}`
        : "/logistics-rfqs-sent";
    case "logistics_arrange_required": {
      const quotationId = meta.quotation_id ?? meta.quotationId;
      if (quotationId != null) {
        return `/logistics/rfq/generic?sellerQuoteId=${quotationId}`;
      }
      return "/logistics/rfq/generic";
    }
    case "logistics_quote_submitted":
    case "logistics_quote_ready":
      return submissionId != null
        ? `/logistics-rfqs-sent/${submissionId}/quotes`
        : "/logistics-rfqs-sent";
    case "logistics_quote_accepted":
    case "logistics_quote_rejected":
    case "logistics_delivery_stages_configured":
    case "logistics_create_manifest":
    case "logistics_grn_created":
      return submissionId != null
        ? `/dashboard/logistics/rfqs/${submissionId}/quote`
        : "/dashboard/logistics/rfqs";
    case "logistics_handover_ready":
      return submissionId != null && quoteId != null
        ? `/dashboard/logistics/rfqs/${submissionId}/handover/${quoteId}`
        : submissionId != null
          ? `/dashboard/logistics/rfqs/${submissionId}/quote`
          : "/dashboard/logistics/rfqs";
    case "logistics_eway_generated":
      if (recipientRole === "provider" && submissionId != null) {
        return `/dashboard/logistics/rfqs/${submissionId}/quote`;
      }
      return ewayBillId != null
        ? `/eway-bill-tracking?ewayBillId=${ewayBillId}`
        : submissionId != null
          ? `/logistics-rfqs-sent?rfqId=${submissionId}`
          : "/logistics-rfqs-sent";
    case "logistics_manifest_submitted":
    case "logistics_create_grn":
    case "logistics_manifest_accepted":
      return manifestId != null
        ? `/shipping-manifest-list?manifestId=${manifestId}`
        : submissionId != null
          ? `/logistics-rfqs-sent?rfqId=${submissionId}`
          : "/logistics-rfqs-sent";
    case "logistics_transit_update":
      return submissionId != null && manifestId != null
        ? `/dashboard/logistics/rfqs/${submissionId}/transit/${manifestId}`
        : submissionId != null
          ? `/logistics-rfqs-sent?rfqId=${submissionId}`
          : "/logistics-rfqs-sent";
    case "logistics_grn_accepted":
    case "logistics_invoice_accepted":
    case "logistics_invoice_paid":
      return submissionId != null
        ? `/dashboard/logistics/rfqs/${submissionId}/generate-invoice`
        : "/dashboard/logistics/rfqs";
    default:
      if (recipientRole === "provider" && submissionId != null) {
        return `/dashboard/logistics/rfqs/${submissionId}/quote`;
      }
      if (submissionId != null) return `/logistics-rfqs-sent?rfqId=${submissionId}`;
      return null;
  }
}

export function resolvePlatformNotificationPath(notification) {
  if (!notification || typeof notification !== "object") return null;

  const approvalPath = resolveApprovalNotificationPath(notification);
  if (approvalPath) return approvalPath;

  const auctionPath = resolveAuctionNotificationPath(notification);
  if (auctionPath) return auctionPath;

  const logisticsPath = resolveLogisticsNotificationPath(notification);
  if (logisticsPath) return logisticsPath;

  const direct = notification.redirect_url || notification.redirectUrl;
  if (direct && typeof direct === "string" && direct.trim()) {
    return direct.trim();
  }

  const refType = String(notification.reference_type || notification.referenceType || "").trim();
  const refId = notification.reference_id ?? notification.referenceId;
  const meta = notification.metadata || {};
  if (!refId && !meta.purchase_order_id && !meta.quotation_id) {
    return "/dashboard/notifications";
  }

  switch (refType) {
    case "p2p_approval_request":
      return `/dashboard/p2p/my-approvals?requestId=${refId}`;
    case "p2p_transaction":
      return `/dashboard/p2p/transactions/${refId}`;
    case "rfq":
      return `/buyer-rfq?rfqId=${refId}`;
    case "quotation":
      return `/seller-quotation-list?quotationId=${refId}`;
    case "purchase_order":
      return `/seller-purchase-order-list?poId=${refId}`;
    case "invoice":
      return `/approve-invoice-list?invoiceId=${refId}`;
    case "eway_bill":
      return `/eway-bill-tracking?ewayBillId=${refId}`;
    case "shipping_manifest":
      return `/shipping-manifest-list?manifestId=${refId}`;
    case "grn":
      return `/grn-manifest-list?grnId=${refId}`;
    case "cm_rfq":
    case "cm_quotation":
      return `/dashboard/buyer/contract-manufacturers/quotes?rfqId=${refId}`;
    case "logistics_generic_rfq":
      return `/dashboard/logistics/rfqs?rfqId=${refId}`;
    case "qa_request":
      return `/buyer-qa-request?qaRequestId=${refId}`;
    case "qa_inspection":
      return `/auction/qa/inspection/${refId}`;
    case "auction":
      return `/auction/${refId}`;
    case "infrastructure_enquiry":
      return "/infrastructure-leasing/my-enquiries";
    default:
      return null;
  }
}

export function formatNotificationDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
